/**
 * Document workspaces in the link graph (card 5.4): a workspace is a GROUP
 * node — `doc_workspace:<id>` — that contains the root folders and pages of
 * that workspace. Personal documents only: the device scope and team scopes
 * have no workspaces, and Personal itself stays ungrouped (as it always was),
 * so a user without workspaces sees exactly the graph they saw before.
 *
 * Pure, like graphModel.js: the snapshot's `docWorkspaces` (`{ id, name }`
 * from the personal bucket's docscope rows) and the `scopeId` a personal root
 * row carries go in; nodes and `contains` edges come out through the
 * builder's own `addNode` / `addEdge`.
 */

/** Only a cloud-personal row can name a workspace (device rows share the wire `personal`). */
const inPersonalCloud = (d) => d?.scope === 'personal' && d?.location !== 'device';

/**
 * @param s        the snapshot (`docWorkspaces`, `docNodes`)
 * @param builder  `{ nodes, nodeId, addNode, addEdge }` from buildGraph
 */
export function addDocWorkspaces(s, { nodes, nodeId, addNode, addEdge }) {
  const known = new Map();
  for (const w of s.docWorkspaces || []) {
    if (w?.id == null || w.id === '') continue;
    known.set(String(w.id), String(w.name || '').trim() || 'Workspace');
  }
  if (!known.size) return;
  const docIds = new Set((s.docNodes || []).map((d) => String(d?.id)));
  for (const d of s.docNodes || []) {
    if (!inPersonalCloud(d) || !d.scopeId || !known.has(String(d.scopeId))) continue;
    // A root is a node with no parent, or whose parent is not there (the
    // sidebar shows such an orphan at the top, so the graph does too).
    if (d.parentId != null && docIds.has(String(d.parentId))) continue;
    const child = nodeId(d.type === 'folder' ? 'doc_folder' : 'document', d.id);
    if (!nodes.has(child)) continue;
    const ws = addNode('doc_workspace', d.scopeId, known.get(String(d.scopeId)), { scope: 'personal', ...(d.location ? { location: d.location } : {}) });
    addEdge(ws, child, 'contains', 'derived');
  }
}
